import clsx from 'clsx';
import type { ReactNode, ElementType, ComponentPropsWithoutRef } from 'react';

// Components
import Icon from '@/components/base/Icon';

type NavTabsProps = ComponentPropsWithoutRef<'div'> & {
    children: ReactNode | ReactNode[];
};

const NavTabs = ({ children, ...restProps }: NavTabsProps) => {
    return (
        <div
            role="tablist"
            className="tab-list flex max-w-fit flex-wrap items-center gap-x-8 gap-y-2 border-b border-icoGray-300 dark:border-icoDarkMode-beton"
            {...restProps}>
            {children}
        </div>
    );
};

export type NavTabProps<TAs extends ElementType = 'button'> = {
    as?: TAs;
    name: React.ReactNode;
    icon?: string;
    iconActive?: string;
    isActive?: boolean;
} & DistributiveOmit<ComponentPropsWithoutRef<ElementType extends TAs ? 'button' : TAs>, 'as'>;

const NavTab = <TAs extends ElementType>(props: NavTabProps<TAs>) => {
    const {
        as: Component = 'button',
        name,
        icon,
        className,
        iconActive,
        isActive,
        children,
        ...restProps
    } = props;
    return (
        <Component
            role="tab"
            className={clsx(
                className,
                'relative flex items-center space-x-2.5 pb-2.5 font-GBold text-xl focus:ring-0 focus:ring-offset-0',
                {
                    'text-icoGray-400 dark:text-icoDarkMode-wolf': !isActive,
                    'after:absolute after:-bottom-px after:left-0 after:block after:h-0.5 after:w-full after:bg-icoBlue':
                        isActive
                }
            )}
            {...restProps}>
            <span className="flex items-center space-x-2">
                {icon ? (
                    <Icon
                        className={clsx('text-base', {
                            'text-icoBlue': isActive
                        })}
                        icon={iconActive && isActive ? iconActive : icon}
                    />
                ) : null}
                <span>{name}</span>
            </span>
            {children}
        </Component>
    );
};

NavTabs.NavTab = NavTab;

export default NavTabs;
